import React, { Component } from 'react';
import axios from 'axios';
import {
  Grid,
  Button,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
} from '@mui/material';
import * as ETVConstants from '../ETVConstants.js';
import { Download } from '@mui/icons-material';
import ETVMessagePopup from '../tools/ETVMessagePopup';

let hasMounted = false;

class CanvaDesignsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      accessToken: props.accessToken, // Access token passed as a prop
      designs: [],
      exportFormatsByDesign: {},
      exportFormatErrors: {},
      loading: true,
      error: null,
      downloading: false, // Track if a download is in progress
      selected_filename: '',
      selected_filetype:'',
      selected_content_ratio:'',
      window:'',
      progress_message:null,
    };
  }

  componentDidMount() {
    if (hasMounted) {
      return;
    }
    hasMounted = true;
    this.fetchDesigns();
  }

  componentWillUnmount() {
	if(this.exportStatusTimer) clearInterval(this.exportStatusTimer);
	this.exportStatusTimer = null;
	hasMounted = false;
  }

  handleRetry = () => {
	this.setState({loading:true,error:null},()=>this.fetchDesigns());
  }

  async fetchDesigns() {
    this.showProgress("loading designs...");
    const { accessToken } = this.state;
    try {
        const response = await axios.get( ETVConstants.getServerBase() + '/canva-designs',{headers: {
		  Authorization: `Bearer ${accessToken}`,
		  user_id:localStorage.getItem('etv.user_id'),
		  access_token:localStorage.getItem('etv.access_token'),
		},});
        const loadedDesigns = response.data.items;
        //console.log(JSON.stringify(loadedDesigns));
        this.setState({
          designs: loadedDesigns,
          loading: false,
          window:'',
        }, () => this.fetchExportFormats(loadedDesigns));
    } catch (error) {
        this.showInfoMessage("error","error: "+error);
        this.setState({
          error: error.response?.data || error.message || 'Failed to fetch designs',
          loading: false,
        });
    }
  }

    createSafeFilename(unsafe, filetype) {
      let safeFilename = String(unsafe || 'undefined').trim(); // Convert + fallback

      // Replace unsafe characters with underscore
      safeFilename = safeFilename.replace(/[\\/:*?"<>| ]+/g, '_');

      // Remove trailing dots/underscores
      safeFilename = safeFilename.replace(/[._]+$/, '');

      // Fallback again if it’s still empty (e.g. if input was only symbols or whitespace)
      if (!safeFilename) {
        safeFilename = 'undefined';
      }
      return safeFilename + '.' + filetype;
    }


  successDownload(url, thumbnailUrl) {
    this.setState({ downloading: false });
    this.props.exportSuccess(url, thumbnailUrl, this.state.selected_filename, this.state.selected_filetype, this.state.selected_content_ratio);
  }

  failedDownload(reason) {
    this.setState({ downloading: false });
	if(this.props.exportFailure) this.props.exportFailure(reason);
  }

  calculateAspectRatio(width, height) {
    if (!Number.isFinite(Number(width)) || !Number.isFinite(Number(height)) || Number(width) <= 0 || Number(height) <= 0) {
      return 0;
    }
    const ratio = Number(width) / Number(height);
    return Math.round(ratio * 100) / 100;
  }

  async fetchExportFormats(designs) {
    const { accessToken } = this.state;
    const requestHeaders = {
      Authorization: `Bearer ${accessToken}`,
      user_id: localStorage.getItem('etv.user_id'),
      access_token: localStorage.getItem('etv.access_token'),
    };

    await Promise.all((designs || []).map(async (design) => {
      try {
        const response = await axios.get(
          `${ETVConstants.getServerBase()}/canva-designs?design_id=${encodeURIComponent(design.id)}`,
          { headers: requestHeaders },
        );
        const available = response.data?.formats || {};
        // Canva may also permit image exports for an animated design. For the
        // content library, prefer its native video form whenever MP4 exists.
        const formats = available.mp4 ? ['mp4'] : ['jpg', 'png'].filter((format) => available[format]);
        this.setState((state) => ({
          exportFormatsByDesign: {
            ...state.exportFormatsByDesign,
            [design.id]: formats,
          },
        }));
      } catch (error) {
        this.setState((state) => ({
          exportFormatErrors: {
            ...state.exportFormatErrors,
            [design.id]: true,
          },
        }));
      }
    }));
  }

  getExportFormats(design) {
    return this.state.exportFormatsByDesign[design.id];
  }

  downloadDesign(design, filetype) {
    this.showProgress("downloading design");
    this.setState({
      downloading: true,
      selected_filename: this.createSafeFilename(design.title || 'untitled', filetype),
      selected_filetype: filetype,
      selected_content_ratio: this.calculateAspectRatio(design.thumbnail?.width, design.thumbnail?.height),
    });

    const reqUserID = localStorage.getItem('etv.user_id');
    const baseUrl = ETVConstants.getServerBase();
    const accessToken = this.state.accessToken;
	const appAccessToken = localStorage.getItem('etv.access_token');
	const requestHeaders = {user_id:reqUserID,access_token:appAccessToken,Authorization:`Bearer ${accessToken}`};
    const urlStr = `${baseUrl}/canva-download-design?design_id=${encodeURIComponent(design.id)}&export_format=${encodeURIComponent(filetype)}`;
    //console.log('Initiating design export job: ', urlStr);

	fetch(urlStr, { headers:requestHeaders })
	  .then((res) => { if(!res.ok) throw new Error(`Export request failed (${res.status})`); return res.json(); })
      .then((result) => {
        if (result.job) {
          const jobId = result.job.id;
		  let attempts = 0;
          this.exportStatusTimer = setInterval(() => {
			attempts += 1;
			if(attempts > 120) {
			  clearInterval(this.exportStatusTimer);
			  this.exportStatusTimer = null;
			  this.failedDownload('Canva export timed out.');
			  this.showInfoMessage('Error','Canva export timed out. Please try again.');
			  return;
			}
            const statusUrl = `${baseUrl}/canva-download-design?export_id=${encodeURIComponent(jobId)}`;
            this.showProgress("getting status ");
			fetch(statusUrl, { headers:requestHeaders })
			  .then((res) => { if(!res.ok) throw new Error(`Export status failed (${res.status})`); return res.json(); })
              .then((statusResult) => {
                if (statusResult.job) {
                  const jobStatus = statusResult.job.status;

                  if (jobStatus === 'success') {
                    const downloadUrl = statusResult.job.urls[0];
					clearInterval(this.exportStatusTimer);
					this.exportStatusTimer = null;
                    hasMounted = false;
                    this.successDownload(downloadUrl, design.thumbnail?design.thumbnail.url:null);
                  } else if (jobStatus === 'failed') {
					clearInterval(this.exportStatusTimer);
					this.exportStatusTimer = null;
                    hasMounted = false;
                    this.failedDownload(statusResult.job.error);
                  }
                }
              })
              .catch((err) => {
                this.showInfoMessage("error","failed to download: "+err);
				clearInterval(this.exportStatusTimer);
				this.exportStatusTimer = null;
				this.failedDownload(err.message);
              });
          }, 1000);
		}else throw new Error(result.error || 'Canva did not create an export job.');
      })
      .catch((error) => {
        this.setState({ downloading: false });
		this.showInfoMessage('Error',error.message || 'Failed to start Canva export.');
		this.failedDownload(error.message);
      });
  }

  showProgress( message) {
		this.state.window==='progress_dialog'? this.setState({progress_message:message}):
		this.setState({window:'progress_dialog', progress_message:message});
	}

  showInfoMessage( title, message) {
    this.setState({window:'message_div',info_title:title, info_message:message});
  }

  closeInfoMessage() {
	this.setState({window:''});
  }

  render() {
    const { designs, loading, error, downloading } = this.state;
    if(loading) {
      return <span/>
    }

    if (error) {
      return (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Button variant="contained" color="primary" onClick={this.handleRetry}>
            Retry
          </Button>
        </div>
      );
    }

    return (
        <div style={{ position:'relative', minHeight:410 }}>
          <Grid container spacing={2} alignItems="stretch" sx={{ flex: '1 0 auto' }}>
            {designs.map((design) => {
              const exportFormats = this.getExportFormats(design);
              const formatError = this.state.exportFormatErrors[design.id];
              return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={design.id} sx={{ display:'flex' }}>
                <Card sx={{ width:'100%', maxWidth:350, height:410, margin:'0 auto', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                  <CardMedia
                    component="img"
                    sx={{ width:'100%', height:190, objectFit:'cover', bgcolor:'grey.100' }}
                    image={design.thumbnail ? design.thumbnail.url : ""}
                    alt={design.title || 'Design Thumbnail'}
                  />
                  <CardContent sx={{ flexGrow:1 }}>
                    <Typography variant="h6"  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',}}>{design.title || 'Unnamed Design'}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Created at: {new Date(design.created_at * 1000).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Updated at: {new Date(design.updated_at * 1000).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Pages: {design.page_count}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ minHeight:56, px:2, pb:2, gap:1 }}>
                    {!exportFormats && !formatError && (
                      <Typography variant="caption" color="textSecondary">Checking download format...</Typography>
                    )}
                    {formatError && (
                      <Typography variant="caption" color="error">Download format unavailable</Typography>
                    )}
                    {(exportFormats || []).map((format) => (
                      <Button
                        key={format}
                        variant="contained"
                        startIcon={<Download />}
                        size="small"
                        color="primary"
                        disabled={downloading}
                        onClick={() => this.downloadDesign(design, format)}
                      >
                        {format.toUpperCase()}
                      </Button>
                    ))}
                  </CardActions>
                </Card>
              </Grid>
              );
            })}
          </Grid>
    
          <ETVMessagePopup
            open={this.state.window==='progress_dialog'}
            type="progress"
            title="Canva export"
            message={this.state.progress_message || 'Loading...'}
          />

          <ETVMessagePopup
            open={this.state.window==='message_div'}
            type="error"
            title={this.state.info_title || 'Canva export failed'}
            message={this.state.info_message || 'The Canva export could not be completed.'}
            closeLabel={ETVConstants.trans('close') || 'Close'}
            onClose={()=>this.closeInfoMessage()}
          />

        </div>
     );
    }

}

export default CanvaDesignsList;
